import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import {
  Shield, RefreshCw, CheckCircle2, AlertTriangle, AlertCircle, XCircle,
  Clock, DollarSign, Download, FileText, ChevronRight, ChevronDown,
  Lock, Unlock, User, MessageSquare, ArrowUpRight, Zap, Eye,
  TrendingUp, BarChart3, X, Loader2
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  AreaChart, Area, Legend
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  healthy: { color: "emerald", icon: CheckCircle2, label: "Healthy", bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-700", badge: "bg-emerald-100 text-emerald-700" },
  warning: { color: "amber", icon: AlertTriangle, label: "Warning", bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-700", badge: "bg-amber-100 text-amber-700" },
  critical: { color: "red", icon: XCircle, label: "Critical", bg: "bg-red-50", border: "border-red-200", text: "text-red-700", badge: "bg-red-100 text-red-700" },
  none: { color: "gray", icon: Clock, label: "No Data", bg: "bg-gray-50", border: "border-gray-200", text: "text-gray-500", badge: "bg-gray-100 text-gray-500" },
};

const CHECK_LABELS = {
  accounts_vs_ledger: "Investor Accounts vs Ledger",
  commitments_vs_ledger: "Commitments vs Ledger",
  distributions_vs_ledger: "Distributions vs Ledger",
  fund_allocations_vs_opportunities: "Fund Allocations vs Opportunity Totals",
  fees_vs_ledger: "Platform Fees vs Ledger",
  wallet_vs_capital_states: "Wallet Balances vs Capital States",
};

import { fmtUSD } from "../../utils/currency";

function formatMoney(cents) {
  if (!cents && cents !== 0) return "$0.00";
  return fmtUSD(cents / 100);
}

function timeAgo(iso) {
  if (!iso) return "Never";
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export default function IntegrityDashboard() {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exceptions, setExceptions] = useState({ exceptions: [], total: 0 });
  const [trends, setTrends] = useState(null);
  const [reconHistory, setReconHistory] = useState(null);
  const [frozen, setFrozen] = useState(null);
  const [reconRunning, setReconRunning] = useState(false);
  const [selectedExc, setSelectedExc] = useState(null);
  const [drawerData, setDrawerData] = useState(null);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [actionNotes, setActionNotes] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [excFilters, setExcFilters] = useState({ severity: null, type: null, status: "open", search: "" });
  const [auditData, setAuditData] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const [legacyData, setLegacyData] = useState(null);
  const [cleanupLoading, setCleanupLoading] = useState(false);

  const token = localStorage.getItem("ic_token");
  const headers = { Authorization: `Bearer ${token}` };

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const [dashRes, excRes] = await Promise.all([
        axios.get(`${API}/integrity/dashboard`, { headers }),
        axios.get(`${API}/integrity/exceptions?status=${excFilters.status || "open"}&limit=50`, { headers }),
      ]);
      setDashboard(dashRes.data);
      setExceptions(excRes.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [excFilters.status]);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  const fetchTrends = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/integrity/trends?days=30`, { headers });
      setTrends(res.data);
    } catch { setTrends(null); }
  }, []);
  useEffect(() => { if (activeTab === "dashboard") fetchTrends(); }, [activeTab, fetchTrends]);

  const fetchReconHistory = async () => {
    try {
      const res = await axios.get(`${API}/integrity/reconciliation/history?limit=20`, { headers });
      setReconHistory(res.data.runs);
    } catch { setReconHistory([]); }
  };

  const fetchFrozen = async () => {
    try {
      const res = await axios.get(`${API}/integrity/frozen`, { headers });
      setFrozen(res.data.frozen_entities);
    } catch { setFrozen([]); }
  };

  const runAudit = async () => {
    setAuditLoading(true);
    try {
      const res = await axios.get(`${API}/integrity/audit`, { headers });
      setAuditData(res.data);
    } catch (e) { console.error(e); }
    finally { setAuditLoading(false); }
  };

  const fetchLegacy = async () => {
    try {
      const res = await axios.get(`${API}/integrity/legacy-classification`, { headers });
      setLegacyData(res.data);
    } catch { setLegacyData(null); }
  };

  const bulkCleanup = async (disposition, reason) => {
    setCleanupLoading(true);
    try {
      await axios.post(`${API}/integrity/legacy-cleanup`, { disposition, reason, exception_ids: [] }, { headers });
      await fetchLegacy();
      await fetchDashboard();
    } catch (e) { console.error(e); }
    finally { setCleanupLoading(false); }
  };

  const triggerReconciliation = async () => {
    setReconRunning(true);
    try {
      await axios.post(`${API}/integrity/reconciliation/run`, { notes: "" }, { headers });
      await fetchDashboard();
    } catch (e) { console.error(e); }
    finally { setReconRunning(false); }
  };

  const openDrawer = async (excId) => {
    setSelectedExc(excId);
    setDrawerLoading(true);
    try {
      const res = await axios.get(`${API}/integrity/exceptions/${excId}`, { headers });
      setDrawerData(res.data);
    } catch { setDrawerData(null); }
    finally { setDrawerLoading(false); }
  };

  const performAction = async (excId, action) => {
    setActionLoading(true);
    try {
      await axios.post(`${API}/integrity/exceptions/${excId}/action`, { action, notes: actionNotes }, { headers });
      setActionNotes("");
      if (selectedExc === excId) await openDrawer(excId);
      await fetchDashboard();
    } catch (e) { console.error(e); }
    finally { setActionLoading(false); }
  };

  const exportExceptions = async () => {
    try {
      const res = await axios.get(`${API}/integrity/export-exceptions?status=all`, { headers });
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `exceptions_${new Date().toISOString().slice(0,10)}.json`; a.click();
    } catch (e) { console.error(e); }
  };

  if (loading && !dashboard) return <div className="flex items-center justify-center py-16" data-testid="integrity-loading"><Loader2 className="h-6 w-6 animate-spin text-[#999]" /></div>;

  const d = dashboard || {};
  const kpis = d.kpis || {};
  const platformCfg = STATUS_CONFIG[d.platform_status] || STATUS_CONFIG.none;
  const PlatformIcon = platformCfg.icon;

  return (
    <div className="space-y-5" data-testid="integrity-dashboard">
      {/* 1. PAGE HEADER */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-lg font-bold text-[#111] tracking-tight" data-testid="integrity-title">Capital Integrity Monitoring</h2>
          <p className="text-xs text-[#888] mt-0.5">Monitor reconciliation health, financial exceptions, ledger consistency, and operational anomalies across the platform.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={triggerReconciliation} disabled={reconRunning}
            className="px-3 py-1.5 rounded-lg bg-[#1a3329] text-white text-[10px] font-medium hover:bg-[#2a4d3e] transition-colors disabled:opacity-50 flex items-center gap-1.5"
            data-testid="run-reconciliation-btn">
            {reconRunning ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
            {reconRunning ? "Running..." : "Run Reconciliation"}
          </button>
          <button onClick={exportExceptions}
            className="px-3 py-1.5 rounded-lg border border-[#e8e0d0] text-[10px] font-medium text-[#666] hover:bg-[#faf7f2] transition-colors flex items-center gap-1.5"
            data-testid="export-exceptions-btn">
            <Download className="h-3 w-3" /> Export
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-[#e8e0d0]">
        {[
          { id: "dashboard", label: "Dashboard" },
          { id: "audit", label: "Financial Audit" },
          { id: "legacy", label: "Legacy Cleanup" },
          { id: "history", label: "Reconciliation History" },
          { id: "frozen", label: "Frozen Entities" },
        ].map(t => (
          <button key={t.id} onClick={() => {
            setActiveTab(t.id);
            if (t.id === "history") fetchReconHistory();
            if (t.id === "frozen") fetchFrozen();
            if (t.id === "audit" && !auditData) runAudit();
            if (t.id === "legacy") fetchLegacy();
          }}
            className={`px-3 py-2 text-[11px] font-medium border-b-2 transition-colors ${activeTab === t.id ? "border-emerald-600 text-emerald-700" : "border-transparent text-[#888] hover:text-[#333]"}`}
            data-testid={`tab-${t.id}`}>
            {t.label}
          </button>
        ))}
      </div>

      {activeTab === "dashboard" && <DashboardView d={d} kpis={kpis} platformCfg={platformCfg} PlatformIcon={PlatformIcon}
        exceptions={exceptions} excFilters={excFilters} setExcFilters={setExcFilters} fetchDashboard={fetchDashboard}
        openDrawer={openDrawer} performAction={performAction} trends={trends} />}

      {activeTab === "audit" && <AuditView data={auditData} loading={auditLoading} onRerun={runAudit} />}
      {activeTab === "legacy" && <LegacyCleanupView data={legacyData} loading={cleanupLoading} onCleanup={bulkCleanup} />}
      {activeTab === "history" && <HistoryView runs={reconHistory} />}
      {activeTab === "frozen" && <FrozenView entities={frozen} headers={headers} onRefresh={fetchFrozen} />}

      {/* DETAIL DRAWER */}
      <AnimatePresence>
        {selectedExc && (
          <ExceptionDrawer data={drawerData} loading={drawerLoading} onClose={() => { setSelectedExc(null); setDrawerData(null); }}
            actionNotes={actionNotes} setActionNotes={setActionNotes} performAction={performAction} actionLoading={actionLoading} />
        )}
      </AnimatePresence>
    </div>
  );
}

// ────────────────────────────────────────────────
// DASHBOARD VIEW
// ────────────────────────────────────────────────
function DashboardView({ d, kpis, platformCfg, PlatformIcon, exceptions, excFilters, setExcFilters, fetchDashboard, openDrawer, performAction, trends }) {
  return (
    <div className="space-y-5">
      {/* 2. PLATFORM STATUS BANNER */}
      <div className={`rounded-xl border ${platformCfg.border} ${platformCfg.bg} p-4 flex items-center gap-3`} data-testid="platform-status-banner">
        <div className={`p-2.5 rounded-xl ${platformCfg.badge}`}><PlatformIcon className="h-5 w-5" /></div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className={`text-sm font-bold ${platformCfg.text}`}>{platformCfg.label}</span>
            <span className={`px-2 py-0.5 rounded text-[8px] font-bold uppercase ${platformCfg.badge}`}>{d.platform_status}</span>
          </div>
          <p className="text-[11px] text-[#666] mt-0.5">{d.platform_message}</p>
          <div className="flex items-center gap-4 mt-1.5 text-[10px] text-[#999]">
            <span>Last reconciliation: {kpis.last_reconciliation ? timeAgo(kpis.last_reconciliation) : "Never"}</span>
            <span>Critical: {kpis.critical_exceptions}</span>
            <span>Frozen: {kpis.frozen_entities}</span>
          </div>
        </div>
      </div>

      {/* 3. KPI STRIP */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="kpi-strip">
        <KpiCard label="Open Exceptions" value={kpis.open_exceptions} icon={AlertTriangle} color={kpis.open_exceptions > 0 ? "amber" : "emerald"} />
        <KpiCard label="Critical" value={kpis.critical_exceptions} icon={XCircle} color={kpis.critical_exceptions > 0 ? "red" : "emerald"} />
        <KpiCard label="Last Recon" value={kpis.last_reconciliation ? timeAgo(kpis.last_reconciliation) : "Never"} icon={Clock} color="blue" />
        <KpiCard label="Failed Dists Today" value={kpis.failed_distributions_today} icon={DollarSign} color={kpis.failed_distributions_today > 0 ? "red" : "emerald"} />
        <KpiCard label="In Review" value={kpis.in_review + (kpis.escalated || 0)} icon={Eye} color={kpis.in_review > 0 ? "blue" : "gray"} />
        <KpiCard label="Frozen Entities" value={kpis.frozen_entities} icon={Lock} color={kpis.frozen_entities > 0 ? "red" : "emerald"} />
      </div>

      {/* 4. RECONCILIATION SUMMARY PANEL */}
      <div className="rounded-xl border border-[#e8e0d0] bg-white/80 backdrop-blur-sm" data-testid="reconciliation-panel">
        <div className="px-4 py-3 border-b border-[#e8e0d0] flex items-center justify-between">
          <h3 className="text-xs font-bold text-[#222] uppercase tracking-wider">Reconciliation Monitor</h3>
          <div className="flex items-center gap-2 text-[10px] text-[#999]">
            {d.latest_run?.completed_at && <span>Last run: {timeAgo(d.latest_run.completed_at)} ({d.latest_run.duration_ms}ms)</span>}
          </div>
        </div>
        <div className="divide-y divide-[#f0ebe0]">
          {(d.check_statuses || []).map((check, i) => {
            const cfg = STATUS_CONFIG[check.status] || STATUS_CONFIG.none;
            const Icon = cfg.icon;
            return (
              <div key={check.check_name} className="flex items-center justify-between px-4 py-2.5 hover:bg-[#faf7f2] transition-colors" data-testid={`check-row-${check.check_name}`}>
                <div className="flex items-center gap-2.5">
                  <Icon className={`h-3.5 w-3.5 ${cfg.text}`} />
                  <span className="text-[11px] font-medium text-[#333]">{CHECK_LABELS[check.check_name] || check.check_name}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`px-1.5 py-0.5 rounded text-[8px] font-bold ${cfg.badge}`}>{check.status}</span>
                  {check.mismatch_count > 0 && <span className="text-[10px] text-red-600 font-medium">{check.mismatch_count} mismatches</span>}
                  <span className="text-[9px] text-[#ccc]">{check.duration_ms}ms</span>
                </div>
              </div>
            );
          })}
          {(!d.check_statuses || d.check_statuses.length === 0) && (
            <div className="px-4 py-6 text-center text-xs text-[#999]">No reconciliation data yet. Run a reconciliation to see check statuses.</div>
          )}
        </div>
      </div>

      {/* 5. EXCEPTION QUEUE TABLE */}
      <div className="rounded-xl border border-[#e8e0d0] bg-white/80 backdrop-blur-sm" data-testid="exception-queue-table">
        <div className="px-4 py-3 border-b border-[#e8e0d0] flex items-center justify-between flex-wrap gap-2">
          <h3 className="text-xs font-bold text-[#222] uppercase tracking-wider">Exception Queue</h3>
          <div className="flex items-center gap-1.5 flex-wrap">
            {["open", "in_review", "escalated", "resolved", "all"].map(s => (
              <button key={s} onClick={() => setExcFilters(f => ({ ...f, status: s }))}
                className={`px-2 py-0.5 rounded text-[9px] font-medium transition-colors ${excFilters.status === s ? "bg-emerald-100 text-emerald-700" : "bg-[#f5f0e8] text-[#888] hover:text-[#333]"}`}
                data-testid={`exc-filter-${s}`}>
                {s.replace("_", " ")}
              </button>
            ))}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-[11px]">
            <thead>
              <tr className="bg-[#faf7f2] border-b border-[#e8e0d0]">
                <th className="text-left px-3 py-2 font-semibold text-[#888] uppercase text-[9px]">Severity</th>
                <th className="text-left px-3 py-2 font-semibold text-[#888] uppercase text-[9px]">Type</th>
                <th className="text-left px-3 py-2 font-semibold text-[#888] uppercase text-[9px]">Entity</th>
                <th className="text-right px-3 py-2 font-semibold text-[#888] uppercase text-[9px]">Expected</th>
                <th className="text-right px-3 py-2 font-semibold text-[#888] uppercase text-[9px]">Actual</th>
                <th className="text-left px-3 py-2 font-semibold text-[#888] uppercase text-[9px]">Created</th>
                <th className="text-left px-3 py-2 font-semibold text-[#888] uppercase text-[9px]">Status</th>
                <th className="text-right px-3 py-2 font-semibold text-[#888] uppercase text-[9px]">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#f0ebe0]">
              {exceptions.exceptions.slice(0, 30).map((exc, i) => {
                const sevCfg = exc.severity === "critical" ? STATUS_CONFIG.critical : STATUS_CONFIG.warning;
                const statusCfg = exc.status === "resolved" ? STATUS_CONFIG.healthy : (exc.status === "escalated" ? STATUS_CONFIG.critical : STATUS_CONFIG.warning);
                return (
                  <tr key={exc.exception_id || i} className="hover:bg-[#faf7f2] transition-colors cursor-pointer" onClick={() => exc.exception_id && openDrawer(exc.exception_id)}
                    data-testid={`exc-row-${i}`}>
                    <td className="px-3 py-2"><span className={`px-1.5 py-0.5 rounded text-[8px] font-bold ${sevCfg.badge}`}>{exc.severity}</span></td>
                    <td className="px-3 py-2 text-[#555]">{(exc.type || "").replace(/_/g, " ")}</td>
                    <td className="px-3 py-2">
                      <span className="text-[10px] text-[#333] font-medium">{exc.entity_type}</span>
                      <span className="text-[9px] text-[#bbb] ml-1 font-mono">{(exc.entity_id || "").slice(0, 8)}</span>
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-[#555]">{exc.expected_value_cents != null ? formatMoney(exc.expected_value_cents) : "-"}</td>
                    <td className="px-3 py-2 text-right font-mono text-[#555]">{exc.actual_value_cents != null ? formatMoney(exc.actual_value_cents) : "-"}</td>
                    <td className="px-3 py-2 text-[#999]">{timeAgo(exc.created_at)}</td>
                    <td className="px-3 py-2"><span className={`px-1.5 py-0.5 rounded text-[8px] font-bold ${statusCfg.badge}`}>{exc.status}</span></td>
                    <td className="px-3 py-2 text-right">
                      <div className="flex items-center justify-end gap-1" onClick={e => e.stopPropagation()}>
                        {exc.status === "open" && (
                          <>
                            <button onClick={() => performAction(exc.exception_id, "in_review")} className="px-1.5 py-0.5 rounded text-[8px] font-medium bg-blue-50 text-blue-600 hover:bg-blue-100" data-testid={`review-btn-${i}`}>Review</button>
                            <button onClick={() => performAction(exc.exception_id, "resolve")} className="px-1.5 py-0.5 rounded text-[8px] font-medium bg-emerald-50 text-emerald-600 hover:bg-emerald-100" data-testid={`resolve-btn-${i}`}>Resolve</button>
                            <button onClick={() => performAction(exc.exception_id, "escalate")} className="px-1.5 py-0.5 rounded text-[8px] font-medium bg-red-50 text-red-600 hover:bg-red-100" data-testid={`escalate-btn-${i}`}>Escalate</button>
                          </>
                        )}
                        {exc.status === "in_review" && (
                          <button onClick={() => performAction(exc.exception_id, "resolve")} className="px-1.5 py-0.5 rounded text-[8px] font-medium bg-emerald-50 text-emerald-600 hover:bg-emerald-100">Resolve</button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {exceptions.exceptions.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-[#999]"><CheckCircle2 className="h-8 w-8 text-emerald-300 mx-auto mb-2" />No exceptions found</td></tr>
              )}
            </tbody>
          </table>
        </div>
        {exceptions.total > 30 && <div className="px-4 py-2 text-[10px] text-[#999] border-t border-[#e8e0d0]">Showing 30 of {exceptions.total}</div>}
      </div>

      {/* 6. TREND CHARTS */}
      {trends && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="trend-charts">
          <div className="rounded-xl border border-[#e8e0d0] bg-white/80 p-4">
            <h4 className="text-[10px] font-bold text-[#222] uppercase tracking-wider mb-3">Exceptions Over Time</h4>
            {trends.exceptions_trend.length > 0 ? (
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={trends.exceptions_trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e8e0d0" />
                  <XAxis dataKey="date" tick={{ fontSize: 9, fill: "#999" }} tickFormatter={v => v.slice(5)} />
                  <YAxis tick={{ fontSize: 9, fill: "#999" }} />
                  <Tooltip contentStyle={{ fontSize: 10, borderRadius: 8, border: "1px solid #e8e0d0" }} />
                  <Bar dataKey="critical" fill="#ef4444" stackId="a" radius={[2, 2, 0, 0]} />
                  <Bar dataKey="warning" fill="#f59e0b" stackId="a" radius={[2, 2, 0, 0]} />
                  <Legend iconSize={8} wrapperStyle={{ fontSize: 9 }} />
                </BarChart>
              </ResponsiveContainer>
            ) : <p className="text-[10px] text-[#999] text-center py-8">No data yet</p>}
          </div>
          <div className="rounded-xl border border-[#e8e0d0] bg-white/80 p-4">
            <h4 className="text-[10px] font-bold text-[#222] uppercase tracking-wider mb-3">Reconciliation History</h4>
            {trends.reconciliation_trend.length > 0 ? (
              <ResponsiveContainer width="100%" height={180}>
                <AreaChart data={trends.reconciliation_trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e8e0d0" />
                  <XAxis dataKey="date" tick={{ fontSize: 9, fill: "#999" }} tickFormatter={v => v.slice(5)} />
                  <YAxis tick={{ fontSize: 9, fill: "#999" }} />
                  <Tooltip contentStyle={{ fontSize: 10, borderRadius: 8, border: "1px solid #e8e0d0" }} />
                  <Area type="monotone" dataKey="mismatches" fill="#fef3c7" stroke="#f59e0b" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            ) : <p className="text-[10px] text-[#999] text-center py-8">No data yet</p>}
          </div>
        </div>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────
// KPI CARD
// ────────────────────────────────────────────────
function KpiCard({ label, value, icon: Icon, color }) {
  const colors = {
    emerald: "text-emerald-600 bg-emerald-50 border-emerald-100",
    amber: "text-amber-600 bg-amber-50 border-amber-100",
    red: "text-red-600 bg-red-50 border-red-100",
    blue: "text-blue-600 bg-blue-50 border-blue-100",
    gray: "text-gray-500 bg-gray-50 border-gray-100",
  };
  const c = colors[color] || colors.gray;
  return (
    <div className={`rounded-xl border ${c.split(" ")[2]} bg-white/80 p-3`}>
      <div className="flex items-center gap-2 mb-1.5">
        <div className={`p-1.5 rounded-lg ${c.split(" ").slice(0, 2).join(" ")}`}><Icon className="h-3.5 w-3.5" /></div>
        <span className="text-[9px] font-medium text-[#888] uppercase tracking-wider">{label}</span>
      </div>
      <p className="text-lg font-bold text-[#111] pl-0.5">{value}</p>
    </div>
  );
}

// ────────────────────────────────────────────────
// HISTORY VIEW
// ────────────────────────────────────────────────
function HistoryView({ runs }) {
  if (!runs) return <div className="text-center py-8 text-xs text-[#999]" data-testid="history-loading"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></div>;
  return (
    <div className="rounded-xl border border-[#e8e0d0] bg-white/80" data-testid="integrity-history">
      <div className="px-4 py-3 border-b border-[#e8e0d0]">
        <h3 className="text-xs font-bold text-[#222] uppercase tracking-wider">Reconciliation Run History</h3>
      </div>
      <div className="divide-y divide-[#f0ebe0]">
        {runs.length === 0 && <div className="px-4 py-8 text-center text-xs text-[#999]">No reconciliation runs yet</div>}
        {runs.map((run, i) => {
          const cfg = STATUS_CONFIG[run.status] || STATUS_CONFIG.none;
          const Icon = cfg.icon;
          return (
            <div key={run.reconciliation_run_id || i} className="px-4 py-3 hover:bg-[#faf7f2] transition-colors" data-testid={`history-row-${i}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <Icon className={`h-4 w-4 ${cfg.text}`} />
                  <div>
                    <div className="flex items-center gap-2">
                      <span className={`px-1.5 py-0.5 rounded text-[8px] font-bold ${cfg.badge}`}>{run.status}</span>
                      <span className="text-[11px] font-medium text-[#333]">{run.checks_run} checks, {run.mismatch_count} mismatches</span>
                    </div>
                    <p className="text-[9px] text-[#999] mt-0.5">
                      {run.started_at?.slice(0, 16).replace("T", " ")} | {run.duration_ms}ms | {run.initiated_by === "system" ? "Scheduled" : "Manual"}
                      {run.notes && ` | ${run.notes}`}
                    </p>
                  </div>
                </div>
                <span className="text-[9px] font-mono text-[#ccc]">{(run.reconciliation_run_id || "").slice(0, 8)}</span>
              </div>
              {run.summary_counts && Object.keys(run.summary_counts).length > 0 && (
                <div className="flex gap-2 mt-2 ml-7">
                  {Object.entries(run.summary_counts).map(([k, v]) => (
                    <span key={k} className={`px-1.5 py-0.5 rounded text-[8px] ${v > 0 ? "bg-amber-50 text-amber-600" : "bg-emerald-50 text-emerald-600"}`}>
                      {CHECK_LABELS[k]?.split(" ")[0] || k}: {v}
                    </span>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────
// FROZEN ENTITIES VIEW
// ────────────────────────────────────────────────
function FrozenView({ entities, headers, onRefresh }) {
  const unfreezeEntity = async (entityType, entityId) => {
    try {
      await axios.post(`${API}/integrity/frozen/${entityType}/${entityId}/unfreeze`, { notes: "Admin unfreeze" }, { headers });
      onRefresh();
    } catch (e) { console.error(e); }
  };
  if (!entities) return <div className="text-center py-8 text-xs text-[#999]" data-testid="frozen-loading"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></div>;
  return (
    <div className="rounded-xl border border-[#e8e0d0] bg-white/80" data-testid="frozen-entities">
      <div className="px-4 py-3 border-b border-[#e8e0d0]">
        <h3 className="text-xs font-bold text-[#222] uppercase tracking-wider">Frozen Entities</h3>
      </div>
      {entities.length === 0 ? (
        <div className="px-4 py-8 text-center"><CheckCircle2 className="h-8 w-8 text-emerald-300 mx-auto mb-2" /><p className="text-xs text-[#999]">No frozen entities</p></div>
      ) : (
        <div className="divide-y divide-[#f0ebe0]">
          {entities.map((e, i) => (
            <div key={`${e.entity_type}-${e.entity_id}`} className="px-4 py-3 flex items-center justify-between" data-testid={`frozen-row-${i}`}>
              <div className="flex items-center gap-2.5">
                <Lock className="h-4 w-4 text-red-400" />
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] font-medium text-[#333]">{e.entity_type}</span>
                    <span className="text-[9px] font-mono text-[#bbb]">{e.entity_id?.slice(0, 12)}</span>
                  </div>
                  <p className="text-[9px] text-[#999]">
                    Frozen by {e.frozen_by_name || e.frozen_by} | {e.frozen_at?.slice(0, 16).replace("T", " ")}
                    {e.reason && ` | ${e.reason}`}
                  </p>
                </div>
              </div>
              <button onClick={() => unfreezeEntity(e.entity_type, e.entity_id)}
                className="px-2 py-1 rounded-lg border border-emerald-200 text-[9px] font-medium text-emerald-600 hover:bg-emerald-50 flex items-center gap-1"
                data-testid={`unfreeze-btn-${i}`}>
                <Unlock className="h-3 w-3" /> Unfreeze
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────
// EXCEPTION DETAIL DRAWER
// ────────────────────────────────────────────────
function ExceptionDrawer({ data, loading, onClose, actionNotes, setActionNotes, performAction, actionLoading }) {
  if (!data && !loading) return null;
  return (
    <motion.div initial={{ x: 400, opacity: 0 }} animate={{ x: 0, opacity: 1 }} exit={{ x: 400, opacity: 0 }}
      className="fixed top-0 right-0 h-full w-[420px] bg-white border-l border-[#e8e0d0] shadow-2xl z-50 flex flex-col" data-testid="exception-drawer">
      <div className="flex items-center justify-between px-4 py-3 border-b border-[#e8e0d0]">
        <h3 className="text-xs font-bold text-[#222] uppercase tracking-wider">Exception Detail</h3>
        <button onClick={onClose} className="p-1 rounded hover:bg-[#faf7f2]"><X className="h-4 w-4 text-[#888]" /></button>
      </div>
      {loading ? (
        <div className="flex-1 flex items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-[#999]" /></div>
      ) : data ? (
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Header */}
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className={`px-1.5 py-0.5 rounded text-[8px] font-bold ${data.severity === "critical" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>{data.severity}</span>
              <span className={`px-1.5 py-0.5 rounded text-[8px] font-bold ${data.status === "resolved" ? "bg-emerald-100 text-emerald-700" : data.status === "escalated" ? "bg-red-100 text-red-700" : "bg-blue-100 text-blue-700"}`}>{data.status}</span>
            </div>
            <p className="text-sm font-medium text-[#222]">{(data.type || "").replace(/_/g, " ")}</p>
            <p className="text-[10px] text-[#999] mt-0.5 font-mono">{data.exception_id}</p>
          </div>

          {/* Details */}
          <div className="space-y-2">
            <DetailRow label="Entity Type" value={data.entity_type} />
            <DetailRow label="Entity ID" value={data.entity_id} mono />
            <DetailRow label="Expected" value={data.expected_value_cents != null ? formatMoney(data.expected_value_cents) : "-"} />
            <DetailRow label="Actual" value={data.actual_value_cents != null ? formatMoney(data.actual_value_cents) : "-"} />
            <DetailRow label="Detected By" value={data.detected_by || "system"} />
            <DetailRow label="Created" value={data.created_at?.slice(0, 16).replace("T", " ")} />
            {data.assigned_to && <DetailRow label="Assigned To" value={data.assigned_to} />}
            {data.resolution_notes && <DetailRow label="Resolution" value={data.resolution_notes} />}
          </div>

          {/* Metadata */}
          {data.metadata && Object.keys(data.metadata).length > 0 && (
            <div>
              <p className="text-[9px] font-bold text-[#888] uppercase tracking-wider mb-1">Metadata</p>
              <div className="bg-[#faf7f2] rounded-lg p-2.5 text-[10px] text-[#555] font-mono space-y-0.5">
                {Object.entries(data.metadata).map(([k, v]) => <div key={k}><span className="text-[#999]">{k}:</span> {typeof v === "object" ? JSON.stringify(v) : String(v)}</div>)}
              </div>
            </div>
          )}

          {/* Related Audit Events */}
          {data.related_audit_events?.length > 0 && (
            <div>
              <p className="text-[9px] font-bold text-[#888] uppercase tracking-wider mb-1">Audit Trail ({data.related_audit_events.length})</p>
              <div className="space-y-1">
                {data.related_audit_events.slice(0, 5).map((ev, i) => (
                  <div key={i} className="bg-[#faf7f2] rounded-lg p-2 text-[10px]">
                    <span className="font-medium text-[#333]">{ev.action}</span>
                    <span className="text-[#999] ml-2">{ev.created_at?.slice(0, 16).replace("T", " ")}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Related Ledger Events */}
          {data.related_ledger_events?.length > 0 && (
            <div>
              <p className="text-[9px] font-bold text-[#888] uppercase tracking-wider mb-1">Ledger Events ({data.related_ledger_events.length})</p>
              <div className="space-y-1">
                {data.related_ledger_events.slice(0, 5).map((ev, i) => (
                  <div key={i} className="bg-[#faf7f2] rounded-lg p-2 text-[10px]">
                    <span className="font-medium text-[#333]">{ev.event_type}</span>
                    <span className="text-[#999] ml-2">{ev.amount_cents != null ? formatMoney(ev.amount_cents) : ""}</span>
                    <span className="text-[#ccc] ml-2">{ev.created_at?.slice(0, 16).replace("T", " ")}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Notes History */}
          {data.notes_history?.length > 0 && (
            <div>
              <p className="text-[9px] font-bold text-[#888] uppercase tracking-wider mb-1">Notes ({data.notes_history.length})</p>
              <div className="space-y-1">
                {data.notes_history.map((n, i) => (
                  <div key={i} className="bg-blue-50 rounded-lg p-2 text-[10px]">
                    <span className="font-medium text-blue-700">{n.user_name || n.user_id}</span>
                    <span className="text-blue-500 ml-1 text-[9px]">({n.action})</span>
                    <p className="text-[#555] mt-0.5">{n.note}</p>
                    <p className="text-[9px] text-[#bbb] mt-0.5">{n.created_at?.slice(0, 16).replace("T", " ")}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Actions */}
          {data.status !== "resolved" && (
            <div className="border-t border-[#e8e0d0] pt-3">
              <textarea value={actionNotes} onChange={e => setActionNotes(e.target.value)} placeholder="Add notes..." rows={2}
                className="w-full rounded-lg border border-[#e8e0d0] px-3 py-2 text-[11px] focus:border-emerald-400 outline-none mb-2 resize-none" data-testid="drawer-notes" />
              <div className="flex flex-wrap gap-1.5">
                {data.status === "open" && <ActionBtn label="Review" action="in_review" color="blue" excId={data.exception_id} performAction={performAction} loading={actionLoading} />}
                <ActionBtn label="Add Note" action="add_note" color="gray" excId={data.exception_id} performAction={performAction} loading={actionLoading} />
                <ActionBtn label="Freeze" action="freeze" color="red" excId={data.exception_id} performAction={performAction} loading={actionLoading} icon={Lock} />
                {data.is_frozen && <ActionBtn label="Unfreeze" action="unfreeze" color="emerald" excId={data.exception_id} performAction={performAction} loading={actionLoading} icon={Unlock} />}
                <ActionBtn label="Resolve" action="resolve" color="emerald" excId={data.exception_id} performAction={performAction} loading={actionLoading} icon={CheckCircle2} />
                <ActionBtn label="Escalate" action="escalate" color="red" excId={data.exception_id} performAction={performAction} loading={actionLoading} icon={ArrowUpRight} />
              </div>
            </div>
          )}
        </div>
      ) : null}
    </motion.div>
  );
}

function DetailRow({ label, value, mono }) {
  return (
    <div className="flex items-start justify-between">
      <span className="text-[9px] text-[#999] uppercase tracking-wider">{label}</span>
      <span className={`text-[11px] text-[#333] text-right max-w-[60%] break-all ${mono ? "font-mono" : ""}`}>{value || "-"}</span>
    </div>
  );
}

function ActionBtn({ label, action, color, excId, performAction, loading, icon: Icon }) {
  const colors = {
    blue: "border-blue-200 text-blue-600 bg-blue-50 hover:bg-blue-100",
    emerald: "border-emerald-200 text-emerald-600 bg-emerald-50 hover:bg-emerald-100",
    red: "border-red-200 text-red-600 bg-red-50 hover:bg-red-100",
    gray: "border-gray-200 text-gray-600 bg-gray-50 hover:bg-gray-100",
  };
  return (
    <button onClick={() => performAction(excId, action)} disabled={loading}
      className={`px-2 py-1 rounded-lg border text-[9px] font-medium flex items-center gap-1 transition-colors disabled:opacity-50 ${colors[color]}`}
      data-testid={`drawer-action-${action}`}>
      {Icon && <Icon className="h-3 w-3" />} {label}
    </button>
  );
}


// ────────────────────────────────────────────────
// FINANCIAL AUDIT VIEW
// ────────────────────────────────────────────────
function AuditView({ data, loading, onRerun }) {
  if (loading) return <div className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#999]" /></div>;
  if (!data) return <div className="text-center py-12 text-[#888] text-sm">No audit data. Click "Run Audit" to start.</div>;

  const v = data.verdict;
  const verdictCfg = v === "CLEAN" ? { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-700", icon: CheckCircle2 }
    : v === "LEGACY_ISSUES_ONLY" ? { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-700", icon: AlertTriangle }
    : { bg: "bg-red-50", border: "border-red-200", text: "text-red-700", icon: XCircle };
  const VIcon = verdictCfg.icon;

  const sections = data.sections || {};
  const checks = sections.reconciliation_checks || {};
  const excQ = sections.exception_queue || {};
  const capital = sections.capital_summary || {};
  const ledger = sections.ledger_health || {};
  const lastRecon = sections.last_reconciliation || {};

  return (
    <div className="space-y-5" data-testid="audit-view">
      {/* Verdict Banner */}
      <div className={`rounded-xl border ${verdictCfg.border} ${verdictCfg.bg} p-4 flex items-center gap-3`} data-testid="audit-verdict-banner">
        <VIcon className={`h-6 w-6 ${verdictCfg.text}`} />
        <div className="flex-1">
          <div className={`text-sm font-bold ${verdictCfg.text}`}>{data.verdict.replace(/_/g, " ")}</div>
          <div className="text-xs text-[#666] mt-0.5">{data.verdict_detail}</div>
        </div>
        <button onClick={onRerun} className="px-3 py-1.5 rounded-lg bg-[#1a3329] text-white text-[10px] font-medium hover:bg-[#2a4d3e] flex items-center gap-1.5" data-testid="rerun-audit-btn">
          <RefreshCw className="h-3 w-3" /> Re-run Audit
        </button>
      </div>

      {/* Reconciliation Checks */}
      <div className="rounded-xl border border-[#e8e0d0] bg-white p-4">
        <h3 className="text-xs font-bold text-[#333] mb-3">Reconciliation Checks</h3>
        <div className="space-y-2">
          {Object.entries(checks).map(([key, chk]) => {
            const cfg = STATUS_CONFIG[chk.status] || STATUS_CONFIG.none;
            const Ico = cfg.icon;
            return (
              <div key={key} className={`flex items-center justify-between rounded-lg border ${cfg.border} ${cfg.bg} px-3 py-2`} data-testid={`audit-check-${key}`}>
                <div className="flex items-center gap-2">
                  <Ico className={`h-3.5 w-3.5 ${cfg.text}`} />
                  <span className="text-[11px] font-medium text-[#333]">{chk.label}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-[10px] text-[#888]">{chk.mismatch_count} mismatches</span>
                  <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium ${cfg.badge}`}>{chk.status}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* KPI Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: "Total Accounts", value: capital.total_accounts || 0 },
          { label: "Available Capital", value: formatMoney(capital.total_available_capital_cents) },
          { label: "Total Committed", value: formatMoney(capital.total_committed_cents) },
          { label: "Total Distributed", value: formatMoney(capital.total_distributed_cents) },
          { label: "Total Fees", value: formatMoney(capital.total_fees_cents) },
          { label: "Ledger Entries", value: ledger.total_entries || 0 },
          { label: "Open Exceptions", value: excQ.open || 0 },
          { label: "Pre-Ledger Expected", value: excQ.pre_ledger_expected || 0 },
        ].map((k, i) => (
          <div key={i} className="rounded-xl border border-[#e8e0d0] bg-white p-3" data-testid={`audit-kpi-${i}`}>
            <div className="text-[9px] text-[#999] uppercase tracking-wider">{k.label}</div>
            <div className="text-base font-bold text-[#111] mt-1">{k.value}</div>
          </div>
        ))}
      </div>

      {/* Audit Metadata */}
      <div className="rounded-xl border border-[#e8e0d0] bg-[#faf7f2] p-3 text-[10px] text-[#888]">
        Audit ID: {data.audit_id} | Timestamp: {new Date(data.timestamp).toLocaleString()} | Last Recon: {lastRecon.status || "never_run"} ({lastRecon.mismatch_count || 0} mismatches)
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────
// LEGACY CLEANUP VIEW
// ────────────────────────────────────────────────
function LegacyCleanupView({ data, loading, onCleanup }) {
  if (!data) return <div className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#999]" /></div>;

  const summary = data.classification_summary || {};
  const hasUnclassified = (summary.unclassified || 0) > 0;
  const hasNeeds = (summary.needs_investigation || 0) > 0;

  return (
    <div className="space-y-5" data-testid="legacy-cleanup-view">
      {/* Summary */}
      <div className="rounded-xl border border-[#e8e0d0] bg-white p-4">
        <h3 className="text-xs font-bold text-[#333] mb-3">Exception Classification Summary</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
          {Object.entries(summary).map(([key, count]) => (
            <div key={key} className="rounded-lg border border-[#e8e0d0] bg-[#faf7f2] p-3">
              <div className="text-[9px] text-[#999] uppercase tracking-wider">{key.replace(/_/g, " ")}</div>
              <div className="text-lg font-bold text-[#111] mt-1">{count}</div>
            </div>
          ))}
        </div>

        <div className="flex items-center gap-3 text-[11px]">
          <span className="text-[#666]">Legacy: <strong>{data.legacy_count}</strong></span>
          <span className="text-[#666]">Post-Hardening: <strong>{data.post_hardening_count}</strong></span>
          <span className="text-[#666]">New Open: <strong className={data.new_open_mismatches > 0 ? "text-red-600" : "text-emerald-600"}>{data.new_open_mismatches}</strong></span>
        </div>
      </div>

      {/* Recommendation */}
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
        <div className="flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
          <div>
            <div className="text-[11px] font-bold text-amber-800">Cleanup Recommendation</div>
            <div className="text-[11px] text-amber-700 mt-1">{data.cleanup_recommendation}</div>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="rounded-xl border border-[#e8e0d0] bg-white p-4">
        <h3 className="text-xs font-bold text-[#333] mb-3">Bulk Actions</h3>
        <div className="flex flex-wrap gap-2">
          {hasUnclassified && (
            <button onClick={() => onCleanup("pre_ledger_expected", "Bulk classified: Pre-ledger expected mismatch. No data error.")} disabled={loading}
              className="px-3 py-1.5 rounded-lg bg-amber-50 border border-amber-200 text-amber-700 text-[10px] font-medium hover:bg-amber-100 disabled:opacity-50 flex items-center gap-1.5"
              data-testid="bulk-pre-ledger-btn">
              {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <FileText className="h-3 w-3" />}
              Classify All Open as Pre-Ledger Expected
            </button>
          )}
          {hasNeeds && (
            <button onClick={() => onCleanup("needs_investigation", "Flagged for manual investigation")} disabled={loading}
              className="px-3 py-1.5 rounded-lg bg-blue-50 border border-blue-200 text-blue-700 text-[10px] font-medium hover:bg-blue-100 disabled:opacity-50 flex items-center gap-1.5"
              data-testid="bulk-investigate-btn">
              Flag for Investigation
            </button>
          )}
          <button onClick={() => onCleanup("resolved_manually", "Bulk resolved by admin after audit review.")} disabled={loading}
            className="px-3 py-1.5 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700 text-[10px] font-medium hover:bg-emerald-100 disabled:opacity-50 flex items-center gap-1.5"
            data-testid="bulk-resolve-btn">
            {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3" />}
            Bulk Resolve Remaining Open
          </button>
        </div>
      </div>

      {/* New Mismatches Detail */}
      {data.new_open_mismatches > 0 && data.new_mismatches_detail && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4">
          <h3 className="text-xs font-bold text-red-800 mb-3">New Post-Hardening Mismatches ({data.new_open_mismatches})</h3>
          <div className="space-y-2">
            {data.new_mismatches_detail.map((m, i) => (
              <div key={i} className="rounded-lg border border-red-100 bg-white p-2 text-[11px]">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-[10px] text-[#888]">{m.entity_id?.slice(0, 8)}...</span>
                  <span className="text-[#333] font-medium">{m.entity_type}</span>
                  <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-medium ${m.severity === "critical" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>{m.severity}</span>
                </div>
                <div className="text-[#666] mt-1">{m.metadata?.detail || m.detail || "-"}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
